import { useEffect, useState } from "react";
import { Plus, Trash2, Tag, Save, Power, Loader2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const EUR = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

const emptyDraft = {
  code: "",
  label: "",
  onboarding_discount_eur: 0,
  enabled: true,
  valid_until: "",  // ISO date YYYY-MM-DD or empty
  max_uses: "",
};

export default function AdminPromoCodes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [editingCode, setEditingCode] = useState(null);

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get("/admin/promo-codes")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Promo-Codes konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const resetDraft = () => { setDraft(emptyDraft); setEditingCode(null); };

  const startEdit = (it) => {
    setEditingCode(it.code);
    setDraft({
      code: it.code,
      label: it.label || "",
      onboarding_discount_eur: it.onboarding_discount_eur ?? it.setup_fee_eur ?? 0,
      enabled: it.enabled !== false,
      valid_until: it.valid_until ? String(it.valid_until).slice(0, 10) : "",
      max_uses: it.max_uses != null ? String(it.max_uses) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!draft.code.trim() || draft.code.trim().length < 3) {
      toast.error("Code fehlt oder zu kurz (min. 3 Zeichen)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: draft.code.trim().toUpperCase(),
        label: draft.label || "",
        onboarding_discount_eur: Number(draft.onboarding_discount_eur) || 0,
        enabled: !!draft.enabled,
        valid_until: draft.valid_until
          ? new Date(`${draft.valid_until}T23:59:59`).toISOString()
          : null,
        max_uses: draft.max_uses ? Math.max(1, Number(draft.max_uses)) : null,
      };
      await api.post("/admin/promo-codes", payload);
      toast.success(`Promo-Code "${payload.code}" ${editingCode ? "aktualisiert" : "angelegt"}`);
      resetDraft();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Promo-Code "${code}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${encodeURIComponent(code)}`);
      toast.success("Promo-Code gelöscht");
      if (editingCode === code) resetDraft();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const toggle = async (item) => {
    try {
      await api.post("/admin/promo-codes", {
        code: item.code,
        label: item.label || "",
        onboarding_discount_eur: item.onboarding_discount_eur ?? item.setup_fee_eur ?? 0,
        enabled: !item.enabled,
        valid_until: item.valid_until || null,
        max_uses: item.max_uses ?? null,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status konnte nicht geändert werden");
    }
  };

  const inputCls = "h-11 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none";
  const labelCls = "block text-[12px] font-bold text-zinc-300 uppercase tracking-wider mb-1.5";

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        <p className="font-mono text-[12px] text-[#00E575] tracking-[0.3em] uppercase mb-2">Admin</p>
        <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-2 text-zinc-50">Promo-Codes</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Promo-Codes geben einen Rabatt auf die Onboarding-Gebühr beim Abo-Abschluss. Optional mit Ablaufdatum und maximaler Nutzungsanzahl.
        </p>

        {/* Editor */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold flex items-center gap-2 text-zinc-50">
              {editingCode ? <Edit3 size={18} className="text-[#00E575]" /> : <Plus size={18} className="text-[#00E575]" />}
              {editingCode ? `Bearbeite: ${editingCode}` : "Neuer Promo-Code"}
            </h2>
            {editingCode && (
              <button type="button" onClick={resetDraft} className="text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1 text-[12px] font-medium" data-testid="promo-cancel-edit">
                <X size={14} /> Abbrechen
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Code</label>
              <input
                data-testid="promo-code-input"
                value={draft.code}
                disabled={!!editingCode}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="z. B. ULI4711"
                className={`${inputCls} font-mono uppercase tracking-widest ${editingCode ? "opacity-60 cursor-not-allowed" : ""}`}
              />
            </div>
            <div>
              <label className={labelCls}>Label / Beschreibung</label>
              <input
                data-testid="promo-label-input"
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder='z. B. "Frühbucher 250 € Rabatt"'
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Onboarding-Rabatt (€)</label>
              <input
                data-testid="promo-discount-input"
                type="number"
                min="0"
                max="1200"
                step="0.01"
                value={draft.onboarding_discount_eur}
                onChange={(e) => setDraft({ ...draft, onboarding_discount_eur: e.target.value })}
                className={`${inputCls} font-mono`}
              />
              <div className="text-[10.5px] text-zinc-500 mt-1">Wird von der Onboarding-Gebühr abgezogen (max. 1.200 €)</div>
            </div>
            <div>
              <label className={labelCls}>Gültig bis (optional)</label>
              <input
                data-testid="promo-validuntil-input"
                type="date"
                value={draft.valid_until}
                onChange={(e) => setDraft({ ...draft, valid_until: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Max. Nutzungen (optional)</label>
              <input
                data-testid="promo-maxuses-input"
                type="number"
                min="1"
                value={draft.max_uses}
                onChange={(e) => setDraft({ ...draft, max_uses: e.target.value })}
                placeholder="leer = unbegrenzt"
                className={`${inputCls} font-mono`}
              />
            </div>
            <div className="flex items-center pt-6">
              <label className="inline-flex items-center gap-2 text-[13px] text-zinc-200 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="promo-enabled-input"
                  checked={draft.enabled}
                  onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                  className="w-5 h-5 accent-[#00E575]"
                />
                <span className="font-semibold">Aktiv</span>
              </label>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="promo-save-btn"
              className="inline-flex items-center gap-2 bg-[#00E575] text-[#09090B] font-bold px-5 h-11 rounded-xl active:scale-[0.97] glow-green disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {editingCode ? "Änderungen speichern" : "Anlegen"}
            </button>
            {editingCode && (
              <button type="button" onClick={resetDraft} className="h-11 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[12.5px] font-semibold">
                Neu anlegen statt bearbeiten
              </button>
            )}
          </div>
        </div>

        {/* List */}
        <h2 className="font-display text-lg font-bold mb-3 text-zinc-50">Bestehende Codes</h2>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="animate-spin inline text-[#00E575]" size={24} /></div>
        ) : items.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center text-zinc-400 text-sm font-medium">
            Noch keine Promo-Codes angelegt.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const discount = it.onboarding_discount_eur ?? it.setup_fee_eur ?? 0;
              const validUntilStr = it.valid_until
                ? new Date(it.valid_until).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
                : null;
              return (
                <div key={it.code} data-testid={`promo-row-${it.code}`} className="bg-[#18181B] border border-zinc-800 rounded-xl p-3 flex items-center gap-2">
                  <Tag size={16} className={it.enabled ? "text-[#00E575]" : "text-zinc-500"} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold tracking-wider text-zinc-50 text-[14px]">{it.code}</span>
                      {!it.enabled && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300 uppercase tracking-widest font-bold">inaktiv</span>}
                      {validUntilStr && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase font-bold">bis {validUntilStr}</span>}
                      {it.max_uses != null && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30 uppercase font-bold">{it.used_count || 0}/{it.max_uses}</span>}
                    </div>
                    <div className="text-[12px] text-zinc-300 truncate font-medium">{it.label || "(kein Label)"}</div>
                    <div className="text-[12px] text-zinc-300 mt-0.5 font-mono">
                      Rabatt: <span className="text-[#00E575] font-bold">{EUR(discount)}</span>
                    </div>
                  </div>
                  <button type="button" onClick={() => startEdit(it)} data-testid={`promo-edit-${it.code}`} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-[#00E575]" title="Bearbeiten">
                    <Edit3 size={15} />
                  </button>
                  <button type="button" onClick={() => toggle(it)} className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-[#00E575]" title="An/Aus">
                    <Power size={15} />
                  </button>
                  <button type="button" onClick={() => remove(it.code)} data-testid={`promo-delete-${it.code}`} className="p-2 rounded-lg hover:bg-red-500/10 text-zinc-400 hover:text-red-400" title="Löschen">
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
